import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = ['canvas']

  connect() {
    // Legacy pages place canvases outside any charts-controller element and
    // rely on a document-wide scan; guard per canvas so multiple controller
    // instances (or other chart controllers on the page) don't double-init.
    this.setUpAll('.lineChart', (ctx) => this.setUpLineChart(ctx))
    this.setUpAll('.multiLineChart', (ctx) => this.setUpMultiLineChart(ctx))
    this.setUpAll('.revenueMultiLineChart', (ctx) =>
      this.setUpRevenueMultiLineChart(ctx)
    )
    this.setUpAll('.pieChart', (ctx) => this.setUpPieChart(ctx))
  }

  setUpAll(selector, setUp) {
    document.querySelectorAll(selector).forEach((canvas) => {
      if (!Chart.getChart(canvas)) setUp(canvas)
    })
  }

  updateLineChart(event) {
    const [_data, _status, xhr] = event.detail

    this.canvasTarget.innerHTML = xhr.response
    this.setUpLineChart(this.canvasTarget.firstChild)
  }

  updateMultiLineChart(event) {
    const [_data, _status, xhr] = event.detail

    this.canvasTarget.innerHTML = xhr.response
    this.setUpMultiLineChart(this.canvasTarget.firstChild)
  }

  updatePieChart(event) {
    const [_data, _status, xhr] = event.detail

    this.canvasTarget.innerHTML = xhr.response
    this.setUpPieChart(this.canvasTarget.firstChild)
  }

  setUpLineChart(ctx, label) {
    var myLineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: JSON.parse(ctx.dataset.labels),
        datasets: [
          {
            label: ctx.dataset.label,
            lineTension: 0.3,
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            borderColor: '#e74a3b',
            pointRadius: 3,
            pointBackgroundColor: '#e74a3b',
            pointBorderColor: '#e74a3b',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#e74a3b',
            pointHoverBorderColor: '#e74a3b',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            data: JSON.parse(ctx.dataset.data)
          }
        ]
      },
      options: {
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            bodyColor: '#2d3748',
            titleColor: '#1a202c',
            titleMarginBottom: 10,
            titleFontSize: 14,
            borderColor: '#e2e8f0',
            borderWidth: 1,
            xPadding: 15,
            yPadding: 15,
            displayColors: true,
            intersect: false,
            mode: 'index',
            caretPadding: 10,
            callbacks: {
              label: function (context) {
                return context.dataset.label + ': ' + context.parsed.y
              }
            }
          }
        },
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 10,
            right: 25,
            top: 25,
            bottom: 0
          }
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              maxTicksLimit: 7
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              maxTicksLimit: 20,
              padding: 10
            },
            grid: {
              color: 'rgb(234, 236, 244)',
              drawBorder: false,
              borderDash: [2],
              zeroLineBorderDash: [2]
            }
          }
        }
      }
    })
  }

  buildDatasets(all_data, lineColors) {
    return Object.keys(all_data).map((key, i) => {
      const dataArray = all_data[key]
      const formattedKey = key
        .replace(/_/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      return {
        label: formattedKey,
        lineTension: 0.3,
        backgroundColor: 'rgba(78, 115, 223, 0.05)',
        borderColor: lineColors[i],
        pointRadius: 3,
        pointBackgroundColor: lineColors[i],
        pointBorderColor: lineColors[i],
        pointHoverRadius: 3,
        pointHoverBackgroundColor: lineColors[i],
        pointHoverBorderColor: lineColors[i],
        pointHitRadius: 10,
        pointBorderWidth: 2,
        data: dataArray
      }
    })
  }

  buildAnnotationPoints(annotations, labels) {
    const data = labels.map((label) => {
      const annotation = annotations.find((a) => a.date === label)
      return annotation ? annotation.yValue : null
    })

    return {
      label: 'Annotations',
      data: data,
      pointRadius: data.map((value) => (value ? 5 : 0)),
      pointBackgroundColor: data.map((value) =>
        value ? 'rgba(255, 99, 132, 1)' : 'transparent'
      ),
      pointBorderColor: data.map((value) =>
        value ? 'rgba(255, 99, 132, 1)' : 'transparent'
      ),
      pointHoverRadius: data.map((value) => (value ? 7 : 0)),
      pointHoverBackgroundColor: data.map((value) =>
        value ? 'rgba(255, 99, 132, 1)' : 'transparent'
      ),
      pointHoverBorderColor: data.map((value) =>
        value ? 'rgba(255, 99, 132, 1)' : 'transparent'
      ),
      showLine: false
    }
  }

  setUpMultiLineChart(ctx) {
    const allData = JSON.parse(ctx.dataset.allData)
    const lineColors = JSON.parse(ctx.dataset.lineColors)
    const annotations = ctx.dataset.annotations
      ? JSON.parse(ctx.dataset.annotations)
      : []
    const labels = JSON.parse(ctx.dataset.labels)

    const datasets = this.buildDatasets(allData, lineColors)
    if (annotations.length > 0) {
      datasets.push(this.buildAnnotationPoints(annotations, labels))
    }

    var myLineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: datasets
      },
      options: {
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            bodyColor: '#2d3748',
            titleColor: '#1a202c',
            titleMarginBottom: 10,
            titleFontSize: 14,
            borderColor: '#e2e8f0',
            borderWidth: 1,
            xPadding: 15,
            yPadding: 15,
            displayColors: false,
            callbacks: {
              label: function (context) {
                if (
                  context.datasetIndex ===
                  context.chart.data.datasets.length - 1 &&
                  annotations && annotations.length > 0
                ) {
                  const index = context.dataIndex
                  const annotation = annotations.find(
                    (a) => a.date === context.chart.data.labels[index]
                  )
                  if (annotation) {
                    if (annotation.body) {
                      return [annotation.comment, annotation.body]
                    }
                    return [annotation.comment]
                  }
                  return []
                }
                const label = context.dataset.label || ''
                return label + ': ' + context.parsed.y
              },
              title: function (context) {
                if (
                  context[0].datasetIndex ===
                  context[0].chart.data.datasets.length - 1 &&
                  annotations && annotations.length > 0
                ) {
                  return []
                }
                return context[0].label
              }
            }
          }
        },
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 10,
            right: 25,
            top: 25,
            bottom: 0
          }
        },
        scales: {
          x: {
            type: 'category',
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              maxTicksLimit: 7
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              maxTicksLimit: 10,
              padding: 10
            },
            grid: {
              color: 'rgb(234, 236, 244)',
              drawBorder: false,
              borderDash: [2],
              zeroLineBorderDash: [2]
            }
          }
        }
      }
    })
  }

  setUpRevenueMultiLineChart(ctx, label) {
    var myLineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: JSON.parse(ctx.dataset.labels),
        datasets: [
          {
            label: 'Monthly Fees',
            lineTension: 0.3,
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            borderColor: '#DC2F02',
            pointRadius: 3,
            pointBackgroundColor: '#DC2F02',
            pointBorderColor: '#DC2F02',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#DC2F02',
            pointHoverBorderColor: '#DC2F02',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            data: JSON.parse(ctx.dataset.fees)
          },
          {
            label: 'Tickets',
            lineTension: 0.3,
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            borderColor: '#f6c23e',
            pointRadius: 3,
            pointBackgroundColor: '#f6c23e',
            pointBorderColor: '#f6c23e',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#f6c23e',
            pointHoverBorderColor: '#f6c23e',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            data: JSON.parse(ctx.dataset.tickets)
          },
          {
            label: 'Gift Cards',
            lineTension: 0.3,
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            borderColor: '#1cc88a',
            pointRadius: 3,
            pointBackgroundColor: '#1cc88a',
            pointBorderColor: '#1cc88a',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#1cc88a',
            pointHoverBorderColor: '#1cc88a',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            data: JSON.parse(ctx.dataset.giftcards)
          },
          {
            label: 'Orders',
            lineTension: 0.3,
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            borderColor: '#36b9cc',
            pointRadius: 3,
            pointBackgroundColor: '#36b9cc',
            pointBorderColor: '#36b9cc',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#36b9cc',
            pointHoverBorderColor: '#36b9cc',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            data: JSON.parse(ctx.dataset.orders)
          },
          {
            label: 'Total',
            lineTension: 0.3,
            backgroundColor: 'rgba(78, 115, 223, 0.05)',
            borderColor: '#858796',
            pointRadius: 3,
            pointBackgroundColor: '#858796',
            pointBorderColor: '#858796',
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#858796',
            pointHoverBorderColor: '#858796',
            pointHitRadius: 10,
            pointBorderWidth: 2,
            data: JSON.parse(ctx.dataset.totals)
          }
        ]
      },
      options: {
        plugins: {
          legend: {
            display: false
          },
          tooltips: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            bodyFontColor: '#2d3748',
            titleMarginBottom: 10,
            titleFontColor: '#1a202c',
            titleFontSize: 14,
            borderColor: '#e2e8f0',
            borderWidth: 1,
            xPadding: 15,
            yPadding: 15,
            displayColors: true,
            intersect: false,
            mode: 'index',
            caretPadding: 10,
            callbacks: {
              label: function (tooltipItem, chart) {
                var datasetLabel =
                  chart.datasets[tooltipItem.datasetIndex].label || ''
                return (
                  datasetLabel + ': ' + this.number_format(tooltipItem.yLabel)
                )
              }
            }
          }
        },
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 10,
            right: 25,
            top: 25,
            bottom: 0
          }
        },
        scales: {
          xAxes: {
            time: {
              unit: 'date'
            },
            gridLines: {
              display: false,
              drawBorder: false
            },
            ticks: {
              maxTicksLimit: 7
            }
          },
          yAxes: {
            ticks: {
              maxTicksLimit: 5,
              padding: 10
            },
            gridLines: {
              color: 'rgb(234, 236, 244)',
              zeroLineColor: 'rgb(234, 236, 244)',
              drawBorder: false,
              borderDash: [2],
              zeroLineBorderDash: [2]
            }
          }
        }
      }
    })
  }

  setUpPieChart(ctz) {
    var myPieChart = new Chart(ctz, {
      type: 'doughnut',
      data: {
        labels: JSON.parse(ctz.dataset.labels),
        datasets: [
          {
            data: JSON.parse(ctz.dataset.data),
            // If colors are changed, update application helper method.
            backgroundColor: [
              '#e74a3b',  // Red (primary/danger)
              '#f6c23e',  // Yellow (warning)
              '#1cc88a',  // Green (success)
              '#36b9cc',  // Cyan (info)
              '#6f42c1',  // Purple
              '#fd7e14',  // Orange
              '#e83e8c'   // Pink
            ],
            hoverBackgroundColor: [
              '#e74a3b',
              '#f6c23e',
              '#1cc88a',
              '#36b9cc',
              '#6f42c1',
              '#fd7e14',
              '#e83e8c'
            ],
            hoverBorderColor: 'rgba(234, 236, 244, 1)'
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        tooltips: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          bodyFontColor: '#2d3748',
          borderColor: '#e2e8f0',
          borderWidth: 1,
          xPadding: 15,
          yPadding: 15,
          displayColors: false,
          caretPadding: 10
        },
        cutoutPercentage: 80
      }
    })
  }

  number_format(number, decimals, dec_point, thousands_sep) {
    // *     example: number_format(1234.56, 2, ',', ' ');
    // *     return: '1 234,56'
    number = (number + '').replace(',', '').replace(' ', '')
    var n = !isFinite(+number) ? 0 : +number,
      prec = !isFinite(+decimals) ? 0 : Math.abs(decimals),
      sep = typeof thousands_sep === 'undefined' ? ',' : thousands_sep,
      dec = typeof dec_point === 'undefined' ? '.' : dec_point,
      s = '',
      toFixedFix = function (n, prec) {
        var k = Math.pow(10, prec)
        return '' + Math.round(n * k) / k
      }
    // Fix for IE parseFloat(0.55).toFixed(0) = 0;
    s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.')
    if (s[0].length > 3) {
      s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep)
    }
    if ((s[1] || '').length < prec) {
      s[1] = s[1] || ''
      s[1] += new Array(prec - s[1].length + 1).join('0')
    }
    return s.join(dec)
  }
}
